import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import InteractionHints from './InteractionHints';
import { applyFabricTexture, updateTextureZoom, remapExtrudeUVs } from './fabricTexture';
import FabricLoadingOverlay from './FabricLoadingOverlay';

const DEFAULT_CAM = { x: 0, y: 1.09, z: 1.3 };
const SCALE = 0.008;

function createChairGeo(sA, sB, sC, sD) {
  const halfA = sA / 2;
  const halfB = sB / 2;

  // Calculate control point Y so the bezier peak at center actually reaches halfB (full depth)
  // At t=0.5: y = 0.25*(sC-halfB) + 0.75*cpY = halfB → cpY = (5*halfB - sC) / 3
  const cpY = (5 * halfB - sC) / 3;

  const shape = new THREE.Shape();
  shape.moveTo(-halfA, -halfB);
  shape.lineTo(halfA, -halfB);
  shape.lineTo(halfA, sC - halfB);
  shape.bezierCurveTo(halfA, cpY, -halfA, cpY, -halfA, sC - halfB);
  shape.lineTo(-halfA, -halfB);

  const bevel = Math.min(0.006, sD * 0.12);
  const geo = new THREE.ExtrudeGeometry(shape, {
    steps: 1, depth: sD, bevelEnabled: true,
    bevelThickness: bevel, bevelSize: bevel, bevelSegments: 3, curveSegments: 48
  });
  return geo;
}

// Fix wall UVs so fabric stripes run vertically (up/down) on ALL walls —
// sides, back curve, and front — matching the top face direction.
// v = py (height, always vertical), u = blended pz/px (perimeter position).
// The normal-weighted blend keeps the back-curve X↔Z transition seamless.
function fixChairWallUVs(geo) {
  const uvAttr = geo.attributes.uv;
  const posAttr = geo.attributes.position;
  const nAttr = geo.attributes.normal;
  if (!uvAttr || !posAttr || !nAttr) return;
  const inv = 1 / 0.8;
  for (let i = 0; i < uvAttr.count; i++) {
    const ny = Math.abs(nAttr.getY(i));
    if (ny > 0.5) continue; // skip cap (top/bottom) vertices
    const px = posAttr.getX(i);
    const py = posAttr.getY(i);
    const pz = posAttr.getZ(i);
    const nxAbs = Math.abs(nAttr.getX(i));
    const nzAbs = Math.abs(nAttr.getZ(i));
    // Horizontal: blend pz (for X-facing sides) and px (for Z-facing front/back)
    const total = nxAbs + nzAbs || 1;
    const u = (nxAbs * pz + nzAbs * px) / total * inv;
    // Vertical: always map to height so stripes run up/down
    const v = py * inv;
    uvAttr.setXY(i, u, v);
  }
  uvAttr.needsUpdate = true;
}

const ChairCushion3DPreview = ({
  width = 50, fullDepth = 50, straightDepth = 30, thickness = 5, rawThickness,
  fabricColor, fabricImageUrl, fabricZoom = 3, showZip = true,
  zipPlacement = 'back', rawA = 0, rawB = 0, rawC = 0, unit = 'cm',
  activeField = null, pipingOption = 'none', pipingColor = null, pipingImageUrl = null
}) => {
  const containerRef = useRef(null);
  const [fabricLoading, setFabricLoading] = useState(false);
  const [pipingLoading, setPipingLoading] = useState(false);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const cushionRef = useRef(null);
  const dimLinesRef = useRef(null);
  const zipGroupRef = useRef(null);
  const pipingTopRef = useRef(null);
  const pipingBottomRef = useRef(null);
  const sceneRef = useRef(null);
  const animFrameRef = useRef(null);
  const initializedRef = useRef(false);
  const resetCameraRef = useRef(false);
  const containerWidthRef = useRef(600);
  const dimLabelsRef = useRef({ a: null, b: null, c: null });
  const currentDimsRef = useRef(null);
  const targetDimsRef = useRef(null);
  const targetScaleYRef = useRef(1);
  const activeFieldRef = useRef(activeField);
  const sA = Math.max(width * SCALE, 0.1);
  const sB = Math.max(fullDepth * SCALE, 0.1);
  const sC = Math.max(straightDepth * SCALE, 0.05);
  const sD = Math.max(thickness * SCALE, 0.03);
  const color = fabricColor || '#5a8a5a';

  const fabricImageUrlRef = useRef(fabricImageUrl);
  const colorRef = useRef(color);
  const fabricZoomRef = useRef(fabricZoom);

  useEffect(() => { activeFieldRef.current = activeField; }, [activeField]);
  useEffect(() => { fabricImageUrlRef.current = fabricImageUrl; colorRef.current = color; }, [fabricImageUrl, color]);
  useEffect(() => { fabricZoomRef.current = fabricZoom; }, [fabricZoom]);

  // Update target dims when props change
  useEffect(() => {
    targetDimsRef.current = { a: sA, b: sB, c: sC, d: sD };
  }, [sA, sB, sC, sD]);

  useEffect(() => {
    if (!containerRef.current || initializedRef.current) return;
    initializedRef.current = true;
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    containerWidthRef.current = rect.width;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, rect.width / rect.height, 0.01, 100);
    camera.position.set(DEFAULT_CAM.x, DEFAULT_CAM.y, DEFAULT_CAM.z);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setSize(rect.width, rect.height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    container.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enablePan = true;
    controls.screenSpacePanning = true;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.minDistance = 0.15;
    controls.maxDistance = 4;
    controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.PAN, RIGHT: THREE.MOUSE.PAN };
    const isMobileInit = rect.width < 500;
    controls.target.set(0, isMobileInit ? 0.04 : 0, 0);
    controls.addEventListener('start', () => { resetCameraRef.current = false; });
    renderer.domElement.__orbitControls = controls;
    renderer.domElement.__renderFrame = () => renderer.render(scene, camera);
    controlsRef.current = controls;

    scene.add(new THREE.AmbientLight(0xffffff, 0.6));
    const dirLight = new THREE.DirectionalLight(0xffffff, 1.2);
    dirLight.position.set(3, 5, 3);
    dirLight.castShadow = true;
    dirLight.shadow.mapSize.set(1024, 1024);
    scene.add(dirLight);
    scene.add(new THREE.DirectionalLight(0xffffff, 0.4).translateX(-2).translateY(3).translateZ(-1));

    const ground = new THREE.Mesh(new THREE.PlaneGeometry(5, 5), new THREE.ShadowMaterial({ opacity: 0.15 }));
    ground.rotation.x = -Math.PI / 2;
    ground.position.y = -sD / 2 - 0.002;
    ground.receiveShadow = true;
    scene.add(ground);

    // Create cushion
    const geo = createChairGeo(sA, sB, sC, sD);
    geo.rotateX(-Math.PI / 2);
    geo.translate(0, sD / 2, 0);
    remapExtrudeUVs(geo);
    fixChairWallUVs(geo);
    geo.computeVertexNormals();
    const mat = new THREE.MeshStandardMaterial({ color: new THREE.Color(color), roughness: 0.85, metalness: 0.02 });
    const cushion = new THREE.Mesh(geo, mat);
    cushion.castShadow = true;
    scene.add(cushion);
    cushionRef.current = cushion;
    currentDimsRef.current = { a: sA, b: sB, c: sC, d: sD };

    // Zip group
    const zipGroup = new THREE.Group();
    scene.add(zipGroup);
    zipGroupRef.current = zipGroup;

    // Dimension lines
    const aMat = new THREE.MeshBasicMaterial({ color: 0xaaaaaa });
    const bMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const cMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
    const dimArrowGeo = new THREE.ConeGeometry(0.007, 0.015, 6);

    // A line: light grey, closely spaced dots, no arrows
    const aDashes = [];
    for (let i = 0; i < 28; i++) {
      const dash = new THREE.Mesh(new THREE.BoxGeometry(0.005, 0.003, 0.003), aMat);
      scene.add(dash);
      aDashes.push(dash);
    }

    // B line: white, closely spaced dots, with arrows
    const bDashes = [];
    for (let i = 0; i < 28; i++) {
      const dash = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), bMat);
      scene.add(dash);
      bDashes.push(dash);
    }
    const bArrow1 = new THREE.Mesh(dimArrowGeo, bMat);
    const bArrow2 = new THREE.Mesh(dimArrowGeo, bMat);
    scene.add(bArrow1);
    scene.add(bArrow2);

    // C line: white, closely spaced dots, with arrows
    const cDashes = [];
    for (let i = 0; i < 20; i++) {
      const dash = new THREE.Mesh(new THREE.BoxGeometry(0.003, 0.003, 0.005), cMat);
      scene.add(dash);
      cDashes.push(dash);
    }
    const cArrow1 = new THREE.Mesh(dimArrowGeo, cMat);
    const cArrow2 = new THREE.Mesh(dimArrowGeo, cMat);
    scene.add(cArrow1);
    scene.add(cArrow2);

    dimLinesRef.current = { aDashes, bDashes, bArrow1, bArrow2, cDashes, cArrow1, cArrow2, aMat, bMat, cMat };

    // Piping rings (top + bottom)
    const pipingMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.6, metalness: 0.05 });
    const pipingTop = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat);
    const pipingBottom = new THREE.Mesh(new THREE.BufferGeometry(), pipingMat.clone());
    pipingTop.visible = false;
    pipingBottom.visible = false;
    scene.add(pipingTop);
    scene.add(pipingBottom);
    pipingTopRef.current = pipingTop;
    pipingBottomRef.current = pipingBottom;
    sceneRef.current = scene;

    const targetCamPos = new THREE.Vector3();
    resetCameraRef.current = true;

    const animate = () => {
      animFrameRef.current = requestAnimationFrame(animate);
      // Thumbnail mode: hide dimension indicators and zip
      if (typeof window !== 'undefined' && window.__THUMBNAIL_MODE__) {
        const __dl = dimLinesRef.current;
        if (__dl) Object.values(__dl).forEach(v => {
          if (Array.isArray(v)) v.forEach(m => { if (m && m.visible !== undefined) m.visible = false; });
          else if (v && v.visible !== undefined) v.visible = false;
        });

              if (zipGroupRef.current) zipGroupRef.current.visible = false;
}
      controls.update();
      const td = targetDimsRef.current;
      let cd = currentDimsRef.current;
      if (cd && td) {
        const lerpSpeed = 0.1;
        const changed = Math.abs(cd.a - td.a) > 0.0001 || Math.abs(cd.b - td.b) > 0.0001 || Math.abs(cd.c - td.c) > 0.0001 || Math.abs(cd.d - td.d) > 0.0001;
        if (changed) {
          cd.a += (td.a - cd.a) * lerpSpeed;
          cd.b += (td.b - cd.b) * lerpSpeed;
          cd.c += (td.c - cd.c) * lerpSpeed;
          cd.d += (td.d - cd.d) * lerpSpeed;
          cushion.geometry.dispose();
          const geo = createChairGeo(cd.a, cd.b, cd.c, cd.d);
          geo.rotateX(-Math.PI / 2);
          geo.translate(0, cd.d / 2, 0);
          remapExtrudeUVs(geo);
          fixChairWallUVs(geo);
          geo.computeVertexNormals();
          cushion.geometry = geo;
          applyFabricTexture(cushion, fabricImageUrlRef.current, colorRef.current, fabricZoomRef.current);
        }
        ground.position.y = -cd.d / 2 - 0.002;

        // Piping Y tracking — chair cushion is offset by translate(0, sD/2, 0)
        if (pipingTopRef.current && pipingTopRef.current.visible) {
          const pBevel = Math.min(0.006, cd.d * 0.12);
          pipingTopRef.current.position.y = cd.d * 1.5 + pBevel * Math.SQRT1_2;
          pipingBottomRef.current.position.y = cd.d * 0.5 - pBevel * Math.SQRT1_2;
        }

        // Position dimension lines on top of cushion (Bay Window style)
        if (dimLinesRef.current) {
          // Chair cushion geometry: after rotateX(-PI/2) + translate(0, sD/2, 0)
          // top surface is at y = sD + sD/2 = 1.5*sD, plus bevel
          const bevel = Math.min(0.006, cd.d * 0.12);
          const topY = cd.d * 1.5 + bevel + 0.004;
          const halfA = cd.a / 2;
          const halfB = cd.b / 2;
          const curveStartZ = halfB - cd.c;

          const { aDashes, bDashes, bArrow1, bArrow2, cDashes, cArrow1, cArrow2 } = dimLinesRef.current;

          // A line: width dots at curve start (no arrows)
          for (let i = 0; i < aDashes.length; i++) {
            const t = (i + 0.5) / aDashes.length;
            aDashes[i].position.set(-halfA + cd.a * t, topY, curveStartZ);
          }

          // B line: front to back through exact center (X=0), stopping at back curve edge
          // With corrected bezier, curve now reaches -halfB at center
          const bX = 0;
          const backEdgeCenterZ = -halfB;
          const bLength = halfB - backEdgeCenterZ;
          for (let i = 0; i < bDashes.length; i++) {
            const t = (i + 0.5) / bDashes.length;
            bDashes[i].position.set(bX, topY, halfB - bLength * t);
          }
          bArrow1.position.set(bX, topY, halfB);
          bArrow1.rotation.set(Math.PI / 2, 0, 0);
          bArrow2.position.set(bX, topY, backEdgeCenterZ);
          bArrow2.rotation.set(-Math.PI / 2, 0, 0);

          // C line: front to just touching the A width line (curve start)
          const cX = -halfA + cd.a * 0.15;
          const cEndZ = curveStartZ + 0.008;
          const cLen = halfB - cEndZ;
          for (let i = 0; i < cDashes.length; i++) {
            const t = (i + 0.5) / cDashes.length;
            cDashes[i].position.set(cX, topY, halfB - cLen * t);
          }
          cArrow1.position.set(cX, topY, halfB);
          cArrow1.rotation.set(Math.PI / 2, 0, 0);
          cArrow2.position.set(cX, topY, cEndZ);
          cArrow2.rotation.set(-Math.PI / 2, 0, 0);
        }

        if (resetCameraRef.current) {
          const maxDim = Math.max(cd.a, cd.b);
          const isMobile = containerWidthRef.current < 500;
          const dist = Math.max(maxDim * 2.7, 0.85);
          camera.position.set(0, dist * 0.71, dist * 0.85);
          controls.target.set(0, isMobile ? cd.d / 4 + 0.04 : cd.d / 4, 0);
          resetCameraRef.current = false;
          controls.saveState();
        }

        // Dimension labels + highlight active field
        const af = activeFieldRef.current;
        const YELLOW = 0xffd700;
        const activeBg = 'rgba(255,215,0,0.92)';
        const defaultBg = 'rgba(255,255,255,0.85)';
        const activeText = '#1a1a1a';
        const defaultText = '#374151';

        // Update line/arrow colors
        if (dimLinesRef.current) {
          dimLinesRef.current.aMat.color.set(af === 'a' ? YELLOW : 0xaaaaaa);
          dimLinesRef.current.bMat.color.set(af === 'b' ? YELLOW : 0xffffff);
          dimLinesRef.current.cMat.color.set(af === 'c' ? YELLOW : 0xffffff);
        }

        const halfA = cd.a / 2;
        const halfB = cd.b / 2;
        if (dimLabelsRef.current.a) {
          const v = new THREE.Vector3(0, cd.d * 0.5, halfB + 0.03);
          v.project(camera);
          dimLabelsRef.current.a.style.left = `${(v.x * 0.5 + 0.5) * containerWidthRef.current}px`;
          dimLabelsRef.current.a.style.top = `${(-v.y * 0.5 + 0.5) * rect.height}px`;
          dimLabelsRef.current.a.style.opacity = '1';
          dimLabelsRef.current.a.style.backgroundColor = af === 'a' ? activeBg : defaultBg;
          dimLabelsRef.current.a.style.color = af === 'a' ? activeText : defaultText;
        }
        if (dimLabelsRef.current.b) {
          const bMidZ = 0;
          const bTopY = cd.d * 1.5 + Math.min(0.006, cd.d * 0.12) + 0.004;
          const v = new THREE.Vector3(0.03, bTopY, bMidZ);
          v.project(camera);
          dimLabelsRef.current.b.style.left = `${(v.x * 0.5 + 0.5) * containerWidthRef.current}px`;
          dimLabelsRef.current.b.style.top = `${(-v.y * 0.5 + 0.5) * rect.height}px`;
          dimLabelsRef.current.b.style.opacity = '1';
          dimLabelsRef.current.b.style.backgroundColor = af === 'b' ? activeBg : defaultBg;
          dimLabelsRef.current.b.style.color = af === 'b' ? activeText : defaultText;
        }
        if (dimLabelsRef.current.c) {
          const cLineX = -halfA + cd.a * 0.15;
          const cMidZ = halfB - cd.c / 2;
          const cTopY = cd.d * 1.5 + Math.min(0.006, cd.d * 0.12) + 0.004;
          const v = new THREE.Vector3(cLineX - 0.03, cTopY, cMidZ);
          v.project(camera);
          dimLabelsRef.current.c.style.left = `${(v.x * 0.5 + 0.5) * containerWidthRef.current}px`;
          dimLabelsRef.current.c.style.top = `${(-v.y * 0.5 + 0.5) * rect.height}px`;
          dimLabelsRef.current.c.style.opacity = '1';
          dimLabelsRef.current.c.style.backgroundColor = af === 'c' ? activeBg : defaultBg;
          dimLabelsRef.current.c.style.color = af === 'c' ? activeText : defaultText;
        }
        // D label hidden per user request
      }
      renderer.render(scene, camera);
    };
    animate();

    const handleResize = () => {
      const r = container.getBoundingClientRect();
      containerWidthRef.current = r.width;
      camera.aspect = r.width / r.height;
      camera.updateProjectionMatrix();
      renderer.setSize(r.width, r.height);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animFrameRef.current);
      renderer.dispose();
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      initializedRef.current = false;
    };
  }, []);

  // Target dims are now updated via the useEffect at the top; animate loop handles lerping
  // Initial geometry set when cushion first created
  useEffect(() => {
    if (!cushionRef.current || !currentDimsRef.current) return;
    // Only update target — animate loop does the lerp
    targetDimsRef.current = { a: sA, b: sB, c: sC, d: sD };
    resetCameraRef.current = true;
  }, [sA, sB, sC, sD]);

  // Update color/texture
  useEffect(() => {
    if (cushionRef.current) {
      const needsLoad = fabricImageUrl && (fabricImageUrl.includes('warwick.com') || fabricImageUrl.startsWith('/api/fabric-images'));
      if (needsLoad) setFabricLoading(true);
      applyFabricTexture(cushionRef.current, fabricImageUrl, color, fabricZoom, () => setFabricLoading(false));
    }
  }, [color, fabricImageUrl]);

  useEffect(() => {
    if (cushionRef.current) updateTextureZoom(cushionRef.current, fabricZoom);
  }, [fabricZoom]);

  // Update piping rings — trace the D-shaped outline on top/bottom faces
  useEffect(() => {
    const pt = pipingTopRef.current;
    const pb = pipingBottomRef.current;
    if (!pt || !pb) return;

    const showPiping = pipingOption !== 'none';
    pt.visible = showPiping;
    pb.visible = showPiping;
    if (!showPiping) { setPipingLoading(false); return; }

    const pColor = pipingOption === 'same' ? (color || '#5a8a5a') : (pipingColor || '#f5f0eb');
    const pImageUrl = pipingOption === 'same' ? fabricImageUrl : pipingImageUrl;
    const needsLoad = pImageUrl && (pImageUrl.includes('warwick.com') || pImageUrl.startsWith('/api/fabric-images'));
    if (needsLoad) setPipingLoading(true);
    let __remaining = 2;
    const __done = () => { __remaining -= 1; if (__remaining <= 0) setPipingLoading(false); };
    applyFabricTexture(pt, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);
    applyFabricTexture(pb, pImageUrl, pColor, fabricZoom, needsLoad ? __done : null);

    const halfA = sA / 2;
    const halfB = sB / 2;
    const cpY = (5 * halfB - sC) / 3;
    const bevel = Math.min(0.006, sD * 0.12);
    const tubeRadius = 0.003;
    const expansion = bevel * Math.SQRT1_2;

    // Reconstruct shape with rounded front corners — larger radius + more samples
    // to avoid outward-expansion spikes during tube geometry generation
    const frontR = Math.min(halfA * 0.15, halfB * 0.15, 0.025);
    const shape = new THREE.Shape();
    // Start past front-left corner (on front edge)
    shape.moveTo(-halfA + frontR, -halfB);
    // Front edge
    shape.lineTo(halfA - frontR, -halfB);
    // Round front-right corner
    shape.quadraticCurveTo(halfA, -halfB, halfA, -halfB + frontR);
    // Right side up to bezier start
    shape.lineTo(halfA, sC - halfB);
    // Back bezier curve
    shape.bezierCurveTo(halfA, cpY, -halfA, cpY, -halfA, sC - halfB);
    // Left side down to near front-left corner
    shape.lineTo(-halfA, -halfB + frontR);
    // Round front-left corner (closes back to start)
    shape.quadraticCurveTo(-halfA, -halfB, -halfA + frontR, -halfB);

    const pts2D = shape.getPoints(128);
    if (pts2D.length > 1 && pts2D[0].distanceTo(pts2D[pts2D.length - 1]) < 0.0001) pts2D.pop();

    // Convert to world XZ: shape X → world X, shape Y → world -Z
    const pts3D = pts2D.map(p => new THREE.Vector3(p.x, 0, -p.y));

    // Expand outward by bevel*cos(45°) so piping sits ON the bevel surface
    const n = pts3D.length;
    const expanded = pts3D.map((pt3, i) => {
      const prev = pts3D[(i - 1 + n) % n];
      const next = pts3D[(i + 1) % n];
      const dx = next.x - prev.x;
      const dz = next.z - prev.z;
      const len = Math.sqrt(dx * dx + dz * dz) || 1;
      const nx = -dz / len;
      const nz = dx / len;
      const dot = nx * pt3.x + nz * pt3.z;
      const sign = dot >= 0 ? 1 : -1;
      return new THREE.Vector3(pt3.x + sign * nx * expansion, 0, pt3.z + sign * nz * expansion);
    });

    class LinearClosedCurve extends THREE.Curve {
      constructor(pts) { super(); this.pts = pts; }
      getPoint(t) {
        const n2 = this.pts.length;
        const f = t * n2;
        const i = Math.floor(f) % n2;
        const a = f - Math.floor(f);
        return new THREE.Vector3().lerpVectors(this.pts[i], this.pts[(i + 1) % n2], a);
      }
    }

    const curve = new LinearClosedCurve(expanded);
    const tubeGeo = new THREE.TubeGeometry(curve, 128, tubeRadius, 8, true);

    pt.geometry.dispose();
    pb.geometry.dispose();
    pt.geometry = tubeGeo;
    pb.geometry = tubeGeo.clone();

    // Chair cushion translated up by sD/2: bottom at sD/2, top at 1.5*sD
    pt.position.y = sD * 1.5 + bevel * Math.SQRT1_2;
    pb.position.y = sD * 0.5 - bevel * Math.SQRT1_2;
  }, [sA, sB, sC, sD, color, pipingOption, pipingColor, pipingImageUrl, fabricImageUrl, fabricZoom]);

  const handleReset = () => { resetCameraRef.current = true; };

  // Build zip on placement or dimension changes
  useEffect(() => {
    if (!zipGroupRef.current) return;
    zipGroupRef.current.visible = showZip;
    if (!showZip) return;
    let lastKey = '';
    const buildZip = () => {
      const cd = currentDimsRef.current;
      if (!cd) return;
      const key = [cd.a, cd.b, cd.c, cd.d, zipPlacement].join(',');
      if (key === lastKey) return;
      lastKey = key;

      const zg = zipGroupRef.current;
      while (zg.children.length) {
        const ch = zg.children[0];
        if (ch.geometry) ch.geometry.dispose();
        if (ch.material) ch.material.dispose();
        zg.remove(ch);
      }

      const halfA = cd.a / 2;
      const halfB = cd.b / 2;
      const isBack = zipPlacement === 'back';

      // Cubic bezier helper for back curve
      const cB = (p0, p1, p2, p3, t) => {
        const m = 1 - t;
        return [
          m*m*m*p0[0] + 3*m*m*t*p1[0] + 3*m*t*t*p2[0] + t*t*t*p3[0],
          m*m*m*p0[1] + 3*m*m*t*p1[1] + 3*m*t*t*p2[1] + t*t*t*p3[1]
        ];
      };

      const N = 48;
      const ePts = [];

      if (isBack) {
        // Back curve: 75% coverage (from t=0.125 to t=0.875 to center it)
        const cpY = (5 * halfB - cd.c) / 3;
        const P0 = [halfA, cd.c - halfB];
        const P1 = [halfA, cpY];
        const P2 = [-halfA, cpY];
        const P3 = [-halfA, cd.c - halfB];
        const tStart = 0.125;
        const tEnd = 0.875;
        for (let i = 0; i <= N; i++) {
          const t = tStart + (tEnd - tStart) * (i / N);
          ePts.push(cB(P0, P1, P2, P3, t));
        }
      } else {
        // Front edge: entire A width
        const steps = Math.max(2, Math.round(cd.a / 0.006));
        for (let i = 0; i <= steps; i++) {
          ePts.push([-halfA + cd.a * (i / steps), -halfB]);
        }
      }

      if (ePts.length < 2) return;

      // Deduplicate
      const deduped = [ePts[0]];
      for (let i = 1; i < ePts.length; i++) {
        const dx = ePts[i][0] - deduped[deduped.length - 1][0];
        const dy = ePts[i][1] - deduped[deduped.length - 1][1];
        if (Math.sqrt(dx * dx + dy * dy) > 0.0002) deduped.push(ePts[i]);
      }
      if (deduped.length < 2) return;

      // Convert shape coords to 3D: (sx, sy) -> (sx, y_center, -sy)
      // Chair cushion is translated up by sD/2, center at y = sD
      const yCenter = cd.d;
      const p3 = deduped.map(([sx, sy]) => new THREE.Vector3(sx, yCenter, -sy));

      // Offset outward from cushion surface — compute all offsets FIRST from original positions
      const OFF = 0.008;
      const offsets = p3.map((_, i) => {
        const prev = p3[Math.max(0, i - 1)];
        const next = p3[Math.min(p3.length - 1, i + 1)];
        const tan = new THREE.Vector3().subVectors(next, prev).normalize();
        const n = new THREE.Vector3(-tan.z, 0, tan.x);
        if (n.dot(p3[i]) < 0) n.negate();
        return n.multiplyScalar(OFF);
      });
      for (let i = 0; i < p3.length; i++) {
        p3[i].add(offsets[i]);
      }

      const curve = new THREE.CatmullRomCurve3(p3, false, 'centripetal', 0.5);

      // Zip track (white tube)
      const trackGeo = new THREE.TubeGeometry(curve, Math.max(48, deduped.length), 0.0035, 6, false);
      const trackMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      zg.add(new THREE.Mesh(trackGeo, trackMat));

      // Zip teeth
      const toothMat = new THREE.MeshBasicMaterial({ color: 0xffffff });
      const totalLen = curve.getLength();
      const spacing = 0.014;
      const nTeeth = Math.floor(totalLen / spacing);
      for (let i = 0; i < nTeeth; i++) {
        const frac = (i + 0.5) / nTeeth;
        const pos = curve.getPoint(frac);
        const tan = curve.getTangent(frac);
        const normal = new THREE.Vector3(-tan.z, 0, tan.x).normalize();
        const side = (i % 2 === 0) ? 1 : -1;
        const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.006, 0.005, 0.004), toothMat);
        tooth.position.copy(pos).addScaledVector(normal, side * 0.007);
        tooth.rotation.y = -Math.atan2(tan.z, tan.x);
        zg.add(tooth);
      }
    };
    buildZip();
    const interval = setInterval(buildZip, 200);
    return () => clearInterval(interval);
  }, [zipPlacement, showZip]);

  return (
    <div className="w-full h-[280px] sm:h-[480px] rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #f5f0eb 0%, #e8e0d8 100%)' }} data-testid="chair-cushion-3d-preview">
      {fabricLoading && <FabricLoadingOverlay />}
      {!fabricLoading && pipingLoading && <FabricLoadingOverlay label="Loading piping..." />}
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div ref={el => dimLabelsRef.current.a = el} data-testid="dim-label-a" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>{rawA > 0 ? `${unit === 'mm' ? rawA * 10 : rawA} ${unit}` : `A ${unit}`}</div>
      <div ref={el => dimLabelsRef.current.b = el} data-testid="dim-label-b" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>{rawB > 0 ? `${unit === 'mm' ? rawB * 10 : rawB} ${unit}` : `B ${unit}`}</div>
      <div ref={el => dimLabelsRef.current.c = el} data-testid="dim-label-c" className="absolute pointer-events-none bg-white/85 backdrop-blur-sm px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-semibold text-gray-700 -translate-x-1/2 whitespace-nowrap shadow-sm" style={{ opacity: 0 }}>{rawC > 0 ? `${unit === 'mm' ? rawC * 10 : rawC} ${unit}` : `C ${unit}`}</div>
      {showZip && (
        <div className="hidden sm:flex absolute top-3 left-3 items-center gap-2 bg-white/85 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-600 pointer-events-none" data-testid="zip-legend">
          <svg width="24" height="6" viewBox="0 0 24 6"><line x1="0" y1="3" x2="24" y2="3" stroke="#333" strokeWidth="2" strokeDasharray="3,2" /></svg>
          <span className="font-medium">Zip on {zipPlacement === 'back' ? 'back edge' : 'front edge'}</span>
        </div>
      )}
      <button onClick={handleReset} className="absolute top-3 right-3 w-8 h-8 bg-white/85 backdrop-blur-sm rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-white transition-colors shadow-sm cursor-pointer" title="Reset view">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 0 1-15.36 6.36"/><path d="M3 12a9 9 0 0 1 15.36-6.36"/><polyline points="21 3 21 9 15 9"/><polyline points="3 21 3 15 9 15"/></svg>
      </button>
      <InteractionHints className="hidden sm:flex absolute bottom-3 left-1/2 -translate-x-1/2 items-center gap-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full text-xs text-gray-500 pointer-events-none whitespace-nowrap" />
    </div>
  );
};

export default ChairCushion3DPreview;
